import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { VISUALIZER_BASE_URL } from "../config/runtime";
import { getSessionId } from "../api/visualizeService";
import { focusUnity } from "../utils/overlayCoords";

const UnityContext = createContext(null);

/** Trim, default, ensure scheme. Does not strip `/api` (callers choose). */
function normalizeVisualizerBase(raw) {
  if (typeof raw !== "string") return "";
  let base = raw.trim();
  if (!base) base = "https://localhost:8001";
  if (!/^https?:\/\//i.test(base)) base = `https://${base}`;
  return base.replace(/\/$/, "");
}

/**
 * WebSocket URL for the visualizer hub route `/api/ws/unity`.
 * Unity opens this inside the iframe (same origin as `/webgl/`); we still set
 * `window.__RTWX_VISUALIZER_WS_URL` on the parent for legacy/debug alignment only—
 * do not open a second WebSocket from React with the same sessionId (hub replaces).
 */
function buildVisualizerWsUrl(base, sessionId) {
  const t = normalizeVisualizerBase(base);
  if (!t) return "";
  let u;
  try {
    u = new URL(t);
  } catch {
    return "";
  }
  const wsProto = u.protocol === "https:" ? "wss:" : "ws:";
  const sid = (sessionId || "").trim();
  const q = sid ? `?sessionId=${encodeURIComponent(sid)}` : "";
  return `${wsProto}//${u.host}/api/ws/unity${q}`;
}

/** Strip trailing `/api` so static `/webgl` is on the service root (not `/api/webgl`). */
function visualizerRootForWebGl(base) {
  const t = normalizeVisualizerBase(base);
  if (!t) return "";
  let root = t;
  if (root.toLowerCase().endsWith("/api")) {
    root = root.slice(0, -4).replace(/\/$/, "");
  }
  return root;
}

/** Iframe URL on the visualizer origin (same-origin WebGL + WebSocket). */
function buildWebGlIframeSrc(base, sessionId) {
  const t = visualizerRootForWebGl(base);
  if (!t) return "";
  const sid = sessionId || "";
  return sid
    ? `${t}/webgl/?sessionId=${encodeURIComponent(sid)}`
    : `${t}/webgl/`;
}

function setLabGlobals(visualizerWsUrl, sessionId) {
  window.__RTWX_VISUALIZER_WS_URL = visualizerWsUrl || "";
  window.__RTWX_SESSION_ID = sessionId || "";
  window.__RTWX_LAB_QUERY = {
    ...(sessionId ? { sessionId } : {}),
  };
}

function clearLabGlobals() {
  delete window.__RTWX_VISUALIZER_WS_URL;
  delete window.__RTWX_SESSION_ID;
  delete window.__RTWX_LAB_QUERY;
}

export const UnityProvider = ({ children }) => {
  const { user: authUser } = useAuth();
  const userId = authUser?.id ?? null;
  const sessionId = getSessionId(userId);
  const { pathname } = useLocation();
  const onModelRoute = pathname.startsWith("/model");

  const [targetContainer, setTargetContainer] = useState(null);
  const [unityReady, setUnityReady] = useState(false);
  const [unityError, setUnityError] = useState(false);
  const [iframeMounted, setIframeMounted] = useState(false);
  const [bootId, setBootId] = useState(0);
  const hadUnityRef = useRef(false);
  const remountRafRef = useRef(0);

  const iframeContainerRef = useRef(null);
  const iframeRef = useRef(null);

  const visualizerWsUrl = buildVisualizerWsUrl(VISUALIZER_BASE_URL, sessionId);
  const webGlIframeSrc = buildWebGlIframeSrc(VISUALIZER_BASE_URL, sessionId);

  useEffect(() => {
    if (onModelRoute) {
      hadUnityRef.current = true;
      setIframeMounted(true);
      return;
    }
    if (!hadUnityRef.current) return;

    let cancelled = false;
    const clearFrame = requestAnimationFrame(() => {
      setIframeMounted(false);
      setUnityReady(false);
      setUnityError(false);
      remountRafRef.current = requestAnimationFrame(() => {
        if (cancelled) return;
        setBootId(n => n + 1);
        setIframeMounted(true);
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(clearFrame);
      cancelAnimationFrame(remountRafRef.current);
    };
  }, [onModelRoute]);

  const handleUnityLoad = useCallback(() => {
    setUnityReady(true);
    setUnityError(false);
  }, []);

  const handleUnityFailure = useCallback(() => {
    setUnityError(true);
    setUnityReady(false);
  }, []);

  useEffect(() => {
    if (!iframeMounted || !webGlIframeSrc) return;
    if (!sessionId) {
      console.error(
        "❌ UnityViewer: No sessionId available — Unity will load but may not process commands",
      );
    }
    console.log("🔗 UnityViewer: WebGL iframe boot context", {
      userId,
      sessionId,
      bootId,
      onModelRoute,
      visualizerWsUrl,
      webGlIframeSrc,
    });
  }, [
    iframeMounted,
    bootId,
    onModelRoute,
    sessionId,
    userId,
    visualizerWsUrl,
    webGlIframeSrc,
  ]);

  useEffect(() => {
    setUnityReady(false);
    setUnityError(false);
  }, [sessionId, bootId]);

  useEffect(() => {
    if (!iframeMounted || !webGlIframeSrc) return;
    const iframe = iframeRef.current;
    if (!iframe) return;
    const onEnter = () => focusUnity(iframeRef);
    iframe.addEventListener("pointerenter", onEnter);
    return () => iframe.removeEventListener("pointerenter", onEnter);
  }, [iframeMounted, webGlIframeSrc, bootId, sessionId]);

  useEffect(() => {
    if (!iframeMounted) {
      clearLabGlobals();
      return;
    }
    setLabGlobals(visualizerWsUrl, sessionId || "");
    return () => {
      clearLabGlobals();
    };
  }, [iframeMounted, sessionId, visualizerWsUrl]);

  useEffect(() => {
    if (!iframeContainerRef.current) return;

    const updatePosition = () => {
      if (targetContainer) {
        const rect = targetContainer.getBoundingClientRect();
        const container = iframeContainerRef.current;
        container.style.position = "absolute";
        container.style.top = `${rect.top}px`;
        container.style.left = `${rect.left}px`;
        container.style.width = `${rect.width}px`;
        container.style.height = `${rect.height}px`;
        container.style.display = "block";
        container.style.pointerEvents = "auto";
        container.style.overflow = "hidden";
        container.style.zIndex = "1";
        const fromAttr = targetContainer.getAttribute("data-unity-radius");
        const fromStyle =
          targetContainer.style.borderRadius ||
          getComputedStyle(targetContainer).borderRadius;
        container.style.borderRadius = fromAttr || fromStyle || "20px";
      } else {
        const container = iframeContainerRef.current;
        container.style.display = "none";
        container.style.pointerEvents = "none";
      }
    };

    updatePosition();

    if (targetContainer) {
      const resizeObserver = new ResizeObserver(updatePosition);
      resizeObserver.observe(targetContainer);
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
      return () => {
        resizeObserver.disconnect();
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [targetContainer]);

  const contextValue = useMemo(
    () => ({
      setTargetContainer,
      iframeRef,
      iframeLoaded: unityReady,
      iframeError: unityError,
      userId,
      sessionId,
    }),
    [unityReady, unityError, userId, sessionId],
  );

  const showIframe = iframeMounted && !!webGlIframeSrc;

  return (
    <UnityContext.Provider value={contextValue}>
      {children}
      <div
        ref={iframeContainerRef}
        style={{
          position: "fixed",
          display: "none",
          pointerEvents: "none",
          overflow: "hidden",
        }}
      >
        {showIframe ? (
          <iframe
            ref={iframeRef}
            key={`${sessionId || "no-session"}-${bootId}`}
            title='RTWX Visualizer (Unity)'
            src={webGlIframeSrc}
            onLoad={handleUnityLoad}
            onError={handleUnityFailure}
            allow='fullscreen; autoplay'
            tabIndex={-1}
            className='rtwx-unity-iframe'
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              display: "block",
              outline: "none",
              boxShadow: "none",
              WebkitTapHighlightColor: "transparent",
            }}
          />
        ) : null}
      </div>
    </UnityContext.Provider>
  );
};

export const useUnityViewer = (isActive = true) => {
  const context = useContext(UnityContext);

  if (!context) {
    throw new Error("useUnityViewer must be used within a UnityProvider");
  }

  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (el && context && isActive) {
      console.log("🎯 Setting Unity container for active tab");
      context.setTargetContainer(el);
    }

    return () => {
      if (context && el && isActive) {
        console.log("🧹 Cleanup: clearing Unity container");
        context.setTargetContainer(null);
      }
    };
  }, [context, isActive]);

  return {
    containerRef,
    iframeLoaded: context.iframeLoaded,
    iframeError: context.iframeError,
    iframeRef: context.iframeRef,
    userId: context.userId,
    sessionId: context.sessionId,
  };
};
